import { Badge, Button, Card, Group, Loader, Stack, Text } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import {
  showError,
  showUpgradeInquiryModal,
  useUpgrade,
} from '#components/Modals'
import { IOrg, useMst } from '#state'
import { pluralize } from '#util'

import { CONTACT_OWNER_NOTE } from './PaidFeatureNote'
import {
  BillingPlan,
  discountLabel,
  formatPlanDate,
  isCancelled,
  isCanceling,
  isExpired,
  isLiveSubscription,
  renewsBeforeCanceling,
  scheduledPlanName,
  STATUS_COLORS,
  STATUS_LABELS,
  STRIPE_TIER,
  subscriptionRate,
} from './plans'
import {
  useCanUpgrade,
  usePortal,
  useWorkspaceBillingPlan,
} from './useBillingPlan'

const nextBillingLine = (plan: BillingPlan): string | null => {
  if (!plan.currentPeriodEnd) return null
  const when = formatPlanDate(plan.currentPeriodEnd)
  return plan.status === 'trialing'
    ? `Billing starts on ${when}`
    : `Renews on ${when}`
}

const renewalLine = (plan: BillingPlan): string | null => {
  if (!plan.cancelAt) return nextBillingLine(plan)
  const expires = formatPlanDate(plan.cancelAt)
  return renewsBeforeCanceling(plan)
    ? `${nextBillingLine(plan)}, then expires on ${expires}`
    : `Expires on ${expires}`
}

const scheduledPlanLine = (plan: BillingPlan): string | null => {
  const scheduled = plan.scheduledPlan
  if (!scheduled) return null
  return `Changes to ${scheduledPlanName(scheduled)} on ${formatPlanDate(scheduled.startsAt)}`
}

const promotionLine = (plan: BillingPlan): string =>
  plan.cancelAt
    ? `Promotional access until ${formatPlanDate(plan.cancelAt)}`
    : 'Promotional access'

const expiredLine = (plan: BillingPlan): string => {
  const ended = plan.cancelAt ?? plan.currentPeriodEnd
  return ended ? `Expired on ${formatPlanDate(ended)}` : 'Expired'
}

const seatLine = (plan: BillingPlan): string | null => {
  if (!plan.seats) return null
  if (plan.promotional) {
    return plan.seatMax
      ? `${plan.seats} of ${plan.seatMax} ${pluralize(plan.seatMax, 'seat')}`
      : `${plan.seats} ${pluralize(plan.seats, 'seat')}`
  }
  if (isCanceling(plan)) return null
  const billed = plan.interval === 'year' ? 'billed yearly' : 'billed monthly'
  const seats = `${plan.seats} ${pluralize(plan.seats, 'seat')}, ${billed}`
  const rate = subscriptionRate(plan)
  return rate ? `${seats} \u2014 ${rate}` : seats
}

const discountLine = (plan: BillingPlan): string | null => {
  const label = discountLabel(plan)
  if (!label) return null
  const endsAt = plan.discount?.endsAt
  return endsAt ? `${label} until ${formatPlanDate(endsAt)}` : label
}

const ManageBillingButton = () => {
  const { pending, openPortal } = usePortal()

  const manage = async () => {
    const { success, message } = await openPortal()
    if (!success) showError({ message })
  }

  return (
    <Button size="sm" variant="default" loading={pending} onClick={manage}>
      Manage billing
    </Button>
  )
}

const ResubscribeButton = () => {
  const { pending, startSelfServe } = useUpgrade()

  return (
    <Button size="sm" loading={pending} onClick={startSelfServe}>
      Renew plan
    </Button>
  )
}

const SubscribedCard = observer(function SubscribedCard({
  plan,
}: {
  plan: BillingPlan
}) {
  const { user } = useMst()
  const canceled = isCancelled(plan) && !plan.promotional

  return (
    <Card>
      <Stack gap="sm">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Stack gap={4}>
            <Group gap="xs">
              <Text fw="bold">{plan.planName ?? 'Free'}</Text>
              <Badge
                color={canceled ? 'orange' : STATUS_COLORS[plan.status]}
                variant="light"
              >
                {canceled ? 'Canceled' : STATUS_LABELS[plan.status]}
              </Badge>
            </Group>
            {plan.promotional && <Text c="dimmed">{promotionLine(plan)}</Text>}
            {user.isCurrentOrgOwner &&
              !plan.promotional &&
              renewalLine(plan) && <Text c="dimmed">{renewalLine(plan)}</Text>}
            {user.isCurrentOrgOwner && scheduledPlanLine(plan) && (
              <Text c="dimmed">{scheduledPlanLine(plan)}</Text>
            )}
            {user.isCurrentOrgOwner && seatLine(plan) && (
              <Text c="dimmed">{seatLine(plan)}</Text>
            )}
            {user.isCurrentOrgOwner && discountLine(plan) && (
              <Text c="dimmed">{discountLine(plan)}</Text>
            )}
          </Stack>
          {(!plan.promotional || plan.cancelAt) &&
            (user.isCurrentOrgOwner ? (
              <ManageBillingButton />
            ) : (
              <Text c="dimmed">Subscription managed by workspace owner</Text>
            ))}
        </Group>
      </Stack>
    </Card>
  )
})

const ExpiredCard = observer(function ExpiredCard({
  plan,
}: {
  plan: BillingPlan
}) {
  const { user } = useMst()

  return (
    <Card>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Stack gap={4}>
          <Group gap="xs">
            <Text fw="bold">{plan.planName ?? 'Free'}</Text>
            <Badge color="red" variant="light">
              Expired
            </Badge>
          </Group>
          <Text c="dimmed">
            {user.isCurrentOrgOwner
              ? expiredLine(plan)
              : 'Ask the workspace owner to renew this workspace.'}
          </Text>
        </Stack>
        {user.isCurrentOrgOwner && (
          <Group gap="xs" wrap="nowrap">
            <ManageBillingButton />
            <ResubscribeButton />
          </Group>
        )}
      </Group>
    </Card>
  )
})

const UnsubscribedCard = observer(function UnsubscribedCard() {
  const { currentOrg } = useMst()
  const { pending, startSelfServe } = useUpgrade()
  const canUpgrade = useCanUpgrade(currentOrg)

  return (
    <Card>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Stack gap={4}>
          <Text fw="bold">No subscription</Text>
          <Text c="dimmed">
            {canUpgrade
              ? 'Choose a plan to add seats and unlock paid features.'
              : CONTACT_OWNER_NOTE}
          </Text>
        </Stack>
        {canUpgrade && (
          <Button size="sm" loading={pending} onClick={startSelfServe}>
            Upgrade
          </Button>
        )}
      </Group>
    </Card>
  )
})

const LegacyPlanCard = ({ org }: { org: IOrg }) =>
  org.tier === 'free' ? (
    <Card>
      <Text fw="bold">Free plan</Text>
      <Text c="dimmed">Up to 3 workspace members</Text>
      <a onClick={() => showUpgradeInquiryModal(org)}>
        Contact us to discuss pricing for our paid plans
      </a>
    </Card>
  ) : (
    <Card>
      <Text fw="bold">Paid plan</Text>
      <Text c="dimmed">Unlimited workspace members</Text>
      <a href="mailto:hello@scripto.live">Contact us about your account</a>
    </Card>
  )

export const StripePlanCard = ({ orgId }: { orgId: string }) => {
  const planQuery = useWorkspaceBillingPlan(orgId)

  if (planQuery.isPending) {
    return (
      <Card>
        <Loader size="sm" />
      </Card>
    )
  }

  if (!planQuery.isSuccess) {
    return (
      <Card>
        <Text c="dimmed">
          We couldn&rsquo;t load billing for this workspace. Try again shortly.
        </Text>
      </Card>
    )
  }

  const plan = planQuery.data

  if (plan && isLiveSubscription(plan)) return <SubscribedCard plan={plan} />
  if (plan && isExpired(plan)) return <ExpiredCard plan={plan} />
  return <UnsubscribedCard />
}

export const PlanInfo = ({ org }: { org: IOrg }) => {
  return (
    <Stack gap="sm">
      <Text fw="bold">Workspace plan</Text>
      {org.tier === STRIPE_TIER ? (
        <StripePlanCard orgId={org.id} />
      ) : (
        <LegacyPlanCard org={org} />
      )}
    </Stack>
  )
}
